import React, { useState } from 'react';
import { useLang } from "../../contexts/LanguageContext";
import './DendriticNode.css';

const DendriticNode = ({ node, targetWord, dictLanguage, targetPOS, level = 0, defaultExpanded = false, isBilingual = false }) => {
  const { t } = useLang();
  
  // Mapping Readerum languages to translation codes (Expanded)
  const getLangCode = (code) => {
    if (!code) return null;
    const map = {
      'eng': 'en',
      'fra': 'fr',
      'ita': 'it',
      'deu': 'de',
      'por': 'pt',
      'esp': 'es',
      'cat': 'ca',
      'glg': 'gl',
      'eus': 'eu'
    };
    return map[code.toLowerCase()] || code;
  };

  const langCode = getLangCode(dictLanguage);
  const translation = langCode ? node.translations?.[langCode] : null;

  // POS Matching Logic
  const checkPOSMatch = (nodePOS, targetCode) => {
    if (!nodePOS || !targetCode) return false;
    const nodeLower = nodePOS.toLowerCase();
    const targetLower = targetCode.toLowerCase();

    const mappings = {
      'n': ['sustantivo', 'noun', 'nombre', 'sust.'],
      'v': ['verbo', 'verb', 'v.'],
      'adj': ['adjetivo', 'adjective', 'adj.'],
      'adv': ['adverbio', 'adverb', 'adv.'],
      'prep': ['preposición', 'preposition', 'prep.', 'adp'],
      'pron': ['pronombre', 'pronoun', 'det', 'determinante'],
      'conj': ['conjunción', 'conjunction', 'conj.'],
      'propn': ['nombre propio', 'proper noun']
    };

    if (nodeLower === targetLower) return true;
    if (mappings[targetLower]?.some(m => nodeLower.includes(m))) return true;
    
    return false;
  };

  const isTarget = node.word.toLowerCase() === targetWord?.toLowerCase();
  const isPOSMatch = checkPOSMatch(node.part_of_speech, targetPOS);
  
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || isTarget || isPOSMatch);
  const [showExtra, setShowExtra] = useState(!isBilingual); // Ocultar si es bilingüe por defecto

  const toggleExpand = () => setIsExpanded(!isExpanded);
  const toggleExtra = (e) => {
    e.stopPropagation();
    setShowExtra(!showExtra);
  };

  // Render phonetics by dialect
  const renderPhonetics = (phonetics) => {
    if (!phonetics || typeof phonetics !== 'object') return null;
    return (
      <div className="dendritic-phonetics">
        {Object.entries(phonetics).map(([lang, val]) => (
          <span key={lang} className="phon-badge" title={t(lang)}>
            /{val}/ <small className="text-muted">{lang.split('-')[1]}</small>
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={`dendritic-node level-${level} ${isTarget ? 'is-target' : ''} ${isPOSMatch ? 'is-pos-match' : ''}`}>
      <div className="node-header" onClick={toggleExpand}>
        <span className="expand-icon">{isExpanded ? '−' : '+'}</span>
        <span className="word-label">
          {isBilingual && translation ? (
            <>
              {translation}
              <span className="original-inline text-muted ms-2" style={{fontSize: '0.85rem', fontWeight: 'normal'}}>
                [{node.word}]
              </span>
            </>
          ) : (
            <>
              {node.word}
              {translation && <span className="translation-inline"> ({translation})</span>}
            </>
          )}
        </span>
        {node.part_of_speech && (
          <span className={`pos-badge pos-${node.part_of_speech.toLowerCase().replace(/[^a-z]/g, '')} ${isPOSMatch ? 'is-pos-match-tag' : ''}`}>
            {node.part_of_speech}
          </span>
        )}
        {isTarget && <span className="target-indicator">📍</span>}
      </div>

      {isExpanded && (
        <div className="node-body">
          <div className="node-meta">
            <div className="d-flex justify-content-between align-items-center mb-1">
              {renderPhonetics(node.phonetics)}
              <button 
                className="toggle-details-btn" 
                onClick={toggleExtra}
                title={showExtra ? t("HIDE_DETAILS") : t("SHOW_DETAILS")}
              >
                {showExtra ? '−' : '🧬'}
              </button>
            </div>
            
            {showExtra && (
              <div className="extra-linguistic-details">
                <div className="etymology-text">
                  <span className="label">{t("LEMMA_MODAL_ETYM")}:</span> {node.etymology}
                </div>
                <div className="morpheme-text">
                  <span className="label">{t("LEMMA_MODAL_MORPH")}:</span> {node.morpheme} ({node.morpheme_type})
                </div>
              </div>
            )}
          </div>

          <div className="node-definitions">
            <ol className="def-list">
              {node.definitions?.map((def, idx) => {
                const defTranslation = langCode ? def.translations?.[langCode] : null;
                const defPOSMatch = checkPOSMatch(def.part_of_speech, targetPOS);
                return (
                  <li key={idx} className={`def-item ${defPOSMatch ? 'is-pos-match' : ''}`}>
                    <div className="meaning">
                      {def.part_of_speech && (
                        <span className={`pos-badge pos-${def.part_of_speech.toLowerCase().replace(/[^a-z]/g, '')} ${defPOSMatch ? 'is-pos-match-tag' : ''}`}>
                          {def.part_of_speech}
                        </span>
                      )}
                      {defTranslation ? (
                        <div className="bilingual-def">
                           <span className="translation-main">{defTranslation}</span>
                           {!isBilingual && <div className="original-sub">{def.meaning}</div>}
                        </div>
                      ) : (
                        def.meaning
                      )}
                    </div>
                    {def.context_example && (
                      <div className="context-example italic">
                        "{def.context_example}"
                      </div>
                    )}
                    {/* Recursion for nested senses */}
                    {def.definitions && def.definitions.length > 0 && (
                      <div className="sub-definitions mt-2">
                        <ol className="def-list-sub">
                            {def.definitions.map((subDef, sIdx) => (
                              <li key={sIdx}>
                                <span className="meaning-sub">{subDef.meaning}</span>
                              </li>
                            ))}
                        </ol>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Recursion for derivative words */}
          {node.derivatives && node.derivatives.length > 0 && (
            <div className="node-children">
              {node.derivatives.map((child, idx) => (
                <DendriticNode 
                  key={idx} 
                  node={child} 
                  targetWord={targetWord} 
                  dictLanguage={dictLanguage}
                  targetPOS={targetPOS}
                  level={level + 1}
                  defaultExpanded={false} 
                  isBilingual={isBilingual}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DendriticNode;
